import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import { createContext, useContext, useReducer, useState } from 'react';
import { Icon, Image, Segment } from 'semantic-ui-react';
import { uniqueKey } from '../../../global/common/keys';
import { ParentContext } from '../../cnr/contexts/ParentContext';
import { fileViewerHandlers, fileViewerInitialState, fileViewerReducer } from '../../cnr/reducers/FileViewerReducer';
import { uploadHandlers, uploadInitialState, uploadReducer } from '../../cnr/reducers/UploadReducer';
import { gEnums } from '../../enums/globalEnums';
import { vsComponentTypes } from '../../viewSettings/enums/vsComponentTypes';
import Wrapper, { wrapperTypes } from '../../wrappers/Wrapper';
import { FormItemContext } from '../forms/FormItem';
import ImageEditor from '../imaging/ImageEditor';
import ImageGallery from '../imaging/ImageGallery';
import ImagesGrid from '../imaging/ImagesGrid';
import ImageDropzone from '../uploading/ImageDropzone';
import UploaderWithProvider from '../uploading/Uploader';
import usePageFunctions from 'global/cnr/contexts/useHelpers/usePageFunctions';

export const FileViewerContext = createContext()

/** Returns the current image value of the fieldProp */
const SemFileViewer = (props) => {

  const appUserAccess = useAppUserAccess()
  const page_fns = usePageFunctions()

  const formItemContext = useContext(FormItemContext)
  const { formItem_state } = formItemContext ?? {}
  const { fip: fip_fis, fieldProps: fieldProps_fis } = formItem_state ?? {}

  const { fileManagmentOpts, fileMode, fieldProps, fip, fromFileManagement } = props ?? {}
  const { fileOptionType, storageType } = fileManagmentOpts ?? {}

  const _fip = fip ? fip : fip_fis

  const _fieldProps = fieldProps ? fieldProps : fieldProps_fis

  let { itemProp } = _fieldProps ?? {}
  const { data } = itemProp ?? {}
  const { storageType: itemStorageType, allowMultiItemSelect, formItemType } = data ?? {}
  const { value: propValue } = _fip ?? {}

  // parentContext
  const parentContext = useContext(ParentContext);
  const { handlers, states } = parentContext ?? {}
  const { paps_state, storage_state } = states
  const { storage_handlers } = handlers ?? {}

  let _storageType = props.storageType
  if (!_storageType && itemStorageType) { _storageType = itemStorageType }


  const init_state_fv = { propValue, paps_state, page_fns, storage_handlers, storage_state, fileMode, storageType: _storageType, fieldProps: _fieldProps, allowMultiItemSelect }
  const [fileViewer_state, fileViewerDispatch] = useReducer(fileViewerReducer, fileViewerInitialState(init_state_fv));
  const fileViewer_handlers = fileViewerHandlers(fileViewerDispatch, fileViewer_state)
  const { propStorageFile, propStorageFiles, fileSelectMode, showUpload } = fileViewer_state

  const [directFile, setDirectFile] = useState()

  let useDirect = false

  switch (formItemType) {
    case gEnums.formItemTypes.fileItemSelectDirect:
      useDirect = true
      break;
    default:
    // nothing
  }

  const handleSelectedImageFile = (asf) => {
    const df = asf[0]
    if (_fip && _fip.onChange) {
      const x = {
        propname: _fip.propname,
        value: df,
        isFileImage: true
      }
      _fip.onChange(null, x)
    }
    setDirectFile(df)
  }

  const allows = {
    add: true,
    confirm: false,
    counter: false,
    manifestSelect: false,
    remove: false,
    select: false,
  }

  switch (fileMode) {
    case gEnums.fileModes.viewManifest:
      allows.counter = true
      allows.remove = true
      allows.manifestSelect = true
      useDirect = true
      break;
    case gEnums.fileModes.view:
      allows.remove = true
      allows.add = true
      break;
    case gEnums.fileModes.viewWithSelectDirect:
      allows.add = true
      break;
    case gEnums.fileModes.viewWithSelect:
      allows.select = true
      break;
    case gEnums.fileModes.viewWithPick:
      allows.confirm = true
      break;
    default:
    // nothing
  }

  const imageEditor = (imageFile, key) => <ImageEditor
    imageFile={imageFile}
    imageEditProps={{ maxWidth: 240, maxHeight: 240 }}
    key={uniqueKey('su', 'ii', key)}
  />

  const upload_dropzone = (imageFile) => <ImageDropzone
    storageType={_storageType}
    handleSelectedImageFile={handleSelectedImageFile}
    imageFile={imageFile}
  />

  const handleShow_fileSelect = () => fileViewer_handlers.handleShow_fileSelect({ key: vsComponentTypes.filePicker, storageType: _storageType, fieldProps })
  const handleUnShow_fileSelect = () => fileViewer_handlers.handleShow_fileSelect()

  const header = () => {
    let caption = itemProp && itemProp.caption ? itemProp.caption : null
    if (!caption && _fip && _fip.label) { caption = _fip.label }
    return <div className='header-flex'>
      <div>{caption}</div>
      <div>
        <div
          onClick={(e) => { fileSelectMode ? handleUnShow_fileSelect() : handleShow_fileSelect(e) }}>
          <Icon
            name={fileSelectMode ? 'delete' : 'exchange'}
            color={fileSelectMode ? 'red' : 'blue'}
          />
        </div>
      </div>
    </div>
  }

  const content = () => {

    if (_fieldProps) {
      if (fileSelectMode) {
        return <ImageGallery
          fip={_fip}
          storageLocationType={gEnums.storageLocationTypes.event}
          storageType={fileSelectMode.storageType}
          viewerProps={props}
        />
      } else {
        if (propStorageFile) {
          const { urls, customMetadata } = propStorageFile ?? {}
          const { full, thumbnail } = urls ?? {}
          const { name } = customMetadata ?? {}
          const src = thumbnail ? thumbnail : full

          switch (_storageType) {
            case gEnums.storageTypes.pdf:
              return <Segment basic style={{ color: 'white' }}>
                <Icon name='file pdf outline' size='huge' color={'blue'} />
                {name}
              </Segment>
            default:
              return <Image src={src} />
          }

        } else if (propStorageFiles) {
          const igProps = {
            gallery: propStorageFiles,
            storagePermissions: {},
            storageType: _storageType,
            origin: 'semFileViewer'
          }
          return <ImagesGrid igProps={igProps} />
        } else {
          if (directFile) {
            if (useDirect) {
              return upload_dropzone(directFile)
            } else {
              return imageEditor(directFile, 'df')
            }
          } else {
            if (useDirect) {
              return upload_dropzone()
            } else {
              return <div>{'No Image Selected'}</div>
            }
          }
        }
      }
    } else {
      return <ImageGallery
        allowMultiItemSelect={allowMultiItemSelect}
        fileManagmentOpts={fileManagmentOpts}
        fip={_fip}
        fromFileManagement={fromFileManagement}
        storageLocationType={gEnums.storageLocationTypes.event}
        storageType={_storageType}
        viewerProps={props} />
    }
  }

  const content_wrapper = () => {

    let showHeader = false

    switch (fileMode) {
      case gEnums.fileModes.viewManifest:
      case gEnums.fileModes.viewWithSelect:
      case gEnums.fileModes.viewWithSelectDirect:
      case gEnums.fileModes.viewWithModify:
      case gEnums.fileModes.viewWithPick:
        showHeader = true
        break;
      default:
      // nothing
    }

    if (showHeader) {
      return <Wrapper
        header={header()}
        content={content()}
        wrapperType={wrapperTypes.padded}
        noMid={true}
      />
    } else {
      return content()
    }


  }

  const uploader = () => <UploaderWithProvider
    handleCancel={fileViewer_handlers.handleShowUpload}
    origin={'semFileViewer'}
    storageLocationType={gEnums.storageLocationTypes.pages}
  />

  const content_withProvider = (c) => <FileViewerContext.Provider value={{ fileViewer_state, fileViewer_handlers }}>
    {c}
  </FileViewerContext.Provider>


  const sfv_content = () => {
    if (showUpload) {
      return content_withProvider(uploader())
    } else {
      return content_withProvider(content_wrapper())
    }
  }

  return sfv_content()
}

export default SemFileViewer